import { ChevronLeft, ChevronRight, ImageIcon, Paperclip, Plus, X } from 'lucide-react';
import { Suspense, lazy, useState } from 'react';

import { mediaGet } from '@/shared/api/generated/media/media';
import { useQuery } from '@tanstack/react-query';
import { AuthImage } from '@/shared/ui/AuthImage';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { RadioGroup } from '@/shared/ui/RadioGroup';
import { Segmented } from '@/shared/ui/Segmented';
import { Select, SelectItem } from '@/shared/ui/Select';
import { Slider } from '@/shared/ui/Slider';
import { Switch } from '@/shared/ui/Switch';
import { Textarea } from '@/shared/ui/Textarea';

import { BlocksInput } from './BlocksInput';
import type { FormField } from './formModel';
import { GroupInput } from './GroupInput';
import { MediaPicker } from './MediaPicker';
import { ReferenceInput } from './ReferenceInput';
import styles from './FieldInput.module.scss';

/**
 * Редактор форматированного текста грузится отдельно.
 *
 * Он тянет за собой ProseMirror — заметный кусок, который не нужен ни списку
 * записей, ни половине форм. Загружать его вместе со всем приложением значит
 * заставлять каждого платить за поле, которого у него может и не быть.
 */
const RichText = lazy(async () => {
  const module = await import('@/shared/ui/RichText');

  return { default: module.RichText };
});

export interface FieldInputProps {
  field: FormField;
  value: unknown;
  /** Полный путь поля для ошибок: `title`, `body.2.text`, `faq.0.q`. */
  errorKey: string;
  /**
   * Все ошибки формы. Вложенным полям — блокам и группам — нужны ошибки их
   * элементов, а разобрать их может только тот, кто знает про вложенность.
   */
  errors: Record<string, string>;
  /** Медиатека принадлежит сайту, и выбирать можно только из его файлов. */
  siteID: string;
  onChange: (value: unknown) => void;
}

/**
 * Одно поле записи в редакторе.
 *
 * Разбор по видам собран в одном месте: правило «какой вид чем редактируется»
 * иначе расползлось бы по экрану, и добавление вида требовало бы искать все
 * места, где виды перечислены.
 */
export function FieldInput({ field, value, errorKey, errors, siteID, onChange }: FieldInputProps) {
  const shared = {
    label: field.name,
    required: field.required,
    error: errors[errorKey],
    hint: hintOf(field, value),
  };

  switch (field.type) {
    case 'group':
      return (
        <Field {...shared}>
          <GroupInput
            field={field}
            value={value}
            errorKey={errorKey}
            errors={errors}
            siteID={siteID}
            onChange={onChange}
          />
        </Field>
      );

    case 'text':
      return (
        <Field {...shared}>
          <Textarea
            rows={6}
            value={asText(value)}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        </Field>
      );

    case 'blocks':
      return (
        <Field {...shared}>
          <BlocksInput
            value={value}
            siteID={siteID}
            errors={errors}
            fieldKey={errorKey}
            allowed={allowedBlocks(field)}
            onChange={onChange}
          />
        </Field>
      );

    case 'richtext':
      return (
        <Field {...shared}>
          <Suspense fallback={<div className={styles['loading']}>Загружаем редактор…</div>}>
            <RichText
              value={asText(value)}
              onChange={(html) => {
                onChange(html);
              }}
            />
          </Suspense>
        </Field>
      );

    case 'number':
      if (
        field.widget === 'slider' &&
        field.rules.min !== undefined &&
        field.rules.max !== undefined
      ) {
        return (
          <Field {...shared}>
            <Slider
              min={field.rules.min}
              max={field.rules.max}
              step={field.rules.integer === true ? 1 : (field.rules.max - field.rules.min) / 100}
              value={typeof value === 'number' ? value : null}
              aria-label={field.name}
              onValueChange={onChange}
            />
          </Field>
        );
      }

      return (
        <Field {...shared}>
          <Input
            type="number"
            value={asNumberText(value)}
            onChange={(event) => {
              // Пустое поле — это отсутствие значения, а не ноль. Ноль —
              // осмысленное число, и подставлять его за человека нельзя.
              const raw = event.target.value;

              onChange(raw === '' ? null : Number(raw));
            }}
          />
        </Field>
      );

    case 'boolean':
      // Переключатель — вид по умолчанию: «показывать в меню» — состояние,
      // а не отметка в перечне. Флажок — по выбору в схеме.
      if (field.widget !== 'checkbox') {
        return (
          <Field {...shared} labelHidden>
            <Switch
              checked={value === true}
              onCheckedChange={(next) => {
                onChange(next);
              }}
            >
              {field.name}
            </Switch>
          </Field>
        );
      }

      return (
        <Field {...shared} labelHidden>
          <Checkbox
            checked={value === true}
            onCheckedChange={(next) => {
              onChange(next === true);
            }}
          >
            {field.name}
          </Checkbox>
        </Field>
      );

    case 'date':
      return (
        <Field {...shared}>
          <Input
            type="date"
            value={asText(value)}
            onChange={(event) => {
              onChange(event.target.value === '' ? null : event.target.value);
            }}
          />
        </Field>
      );

    case 'datetime':
      return (
        <Field {...shared}>
          <Input
            type="datetime-local"
            value={forDateTimeInput(asText(value))}
            onChange={(event) => {
              // Браузер отдаёт время без часового пояса. Дописываем секунды и
              // «Z»: сервер ждёт RFC 3339, а угадывать пояс за человека
              // означало бы сдвигать время при каждом переезде.
              const raw = event.target.value;

              onChange(raw === '' ? null : `${raw}:00Z`);
            }}
          />
        </Field>
      );

    case 'select':
      if (field.widget === 'radio') {
        return (
          <Field {...shared}>
            <RadioGroup
              value={asText(value)}
              onValueChange={onChange}
              options={choices(field).map((choice) => ({ value: choice, label: choice }))}
            />
          </Field>
        );
      }

      if (field.widget === 'segmented') {
        return (
          <Field {...shared}>
            <Segmented
              value={asText(value)}
              onValueChange={onChange}
              aria-label={field.name}
              options={choices(field).map((choice) => ({ value: choice, label: choice }))}
            />
          </Field>
        );
      }

      return (
        <Field {...shared}>
          <Select
            value={asText(value)}
            onValueChange={onChange}
            placeholder="Выберите вариант"
            disabled={choices(field).length === 0}
          >
            {choices(field).map((choice) => (
              <SelectItem key={choice} value={choice}>
                {choice}
              </SelectItem>
            ))}
          </Select>
        </Field>
      );

    case 'url':
      return (
        <Field {...shared}>
          <Input
            value={asText(value)}
            placeholder="https://example.com или /blog/статья"
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        </Field>
      );

    case 'image':
    case 'file':
      return (
        <Field {...shared}>
          {field.options['multiple'] === true ? (
            <MediaListField
              kind={field.type}
              siteID={siteID}
              value={asIDs(value)}
              onChange={onChange}
            />
          ) : (
            <MediaField
              kind={field.type}
              siteID={siteID}
              value={asFirst(value)}
              onChange={onChange}
            />
          )}
        </Field>
      );

    case 'reference':
      return (
        <Field {...shared}>
          <ReferenceInput
            value={value}
            multiple={field.options['multiple'] === true}
            collection={
              typeof field.options['collection'] === 'string' ? field.options['collection'] : ''
            }
            onChange={onChange}
          />
        </Field>
      );

    default:
      if (field.widget === 'color') {
        return (
          <Field {...shared}>
            <div className={styles['color']}>
              <input
                type="color"
                className={styles['color__swatch']}
                aria-label={`${field.name}: выбрать цвет`}
                value={/^#[0-9a-f]{6}$/i.test(asText(value)) ? asText(value) : '#000000'}
                onChange={(event) => {
                  onChange(event.target.value.toUpperCase());
                }}
              />
              <Input
                mono
                value={asText(value)}
                placeholder="#RRGGBB"
                onChange={(event) => {
                  onChange(event.target.value);
                }}
              />
            </div>
          </Field>
        );
      }

      return (
        <Field {...shared}>
          <Input
            mono={field.widget === 'slug'}
            type={field.widget === 'email' ? 'email' : field.widget === 'phone' ? 'tel' : 'text'}
            placeholder={
              field.widget === 'slug'
                ? 'about-us'
                : field.widget === 'email'
                  ? 'name@example.com'
                  : field.widget === 'phone'
                    ? '+7 (900) 000-00-00'
                    : undefined
            }
            value={asText(value)}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        </Field>
      );
  }
}

/**
 * Поле изображения или файла: показывает выбранное и открывает медиатеку.
 *
 * В значении лежит идентификатор файла, а не адрес: адрес зависит от того,
 * как файл отдаётся, и однажды поменяется — а записи со ссылками на него
 * останутся.
 */
function MediaField({
  kind,
  siteID,
  value,
  onChange,
}: {
  kind: 'image' | 'file';
  siteID: string;
  value: string;
  onChange: (value: unknown) => void;
}) {
  const [picking, setPicking] = useState(false);

  const chosen = useQuery({
    queryKey: ['media-item', value],
    enabled: value !== '',
    queryFn: async () => {
      const response = await mediaGet(value);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос файла');
      }

      return response.data;
    },
  });

  const item = chosen.data;

  return (
    <div className={styles['media']}>
      {item !== undefined && (
        <div className={styles['media__chosen']}>
          {item.thumb_url == null ? (
            <Paperclip size={20} />
          ) : (
            <AuthImage
              className={styles['media__thumb']}
              src={item.thumb_url}
              alt={item.alt === '' ? item.name : item.alt}
            />
          )}

          <span className={styles['media__name']}>{item.name}</span>

          <Button
            iconOnly
            size="sm"
            variant="ghost"
            tone="neutral"
            aria-label="Убрать файл из поля"
            onClick={() => {
              onChange(null);
            }}
          >
            <X size={15} />
          </Button>
        </div>
      )}

      <Button
        variant="outline"
        tone="neutral"
        iconLeft={kind === 'image' ? <ImageIcon size={15} /> : <Paperclip size={15} />}
        onClick={() => {
          setPicking(true);
        }}
      >
        {value === '' ? 'Выбрать' : 'Заменить'}
      </Button>

      <MediaPicker
        open={picking}
        onOpenChange={setPicking}
        siteID={siteID}
        kind={kind}
        chosenID={value === '' ? undefined : value}
        onChoose={(picked) => {
          onChange(picked.id);
        }}
      />
    </div>
  );
}

/**
 * Несколько файлов: плитки по порядку, порядок — стрелками, добавить —
 * из медиатеки сразу несколько. Порядок важен: в галерее он и есть порядок
 * на сайте.
 */
function MediaListField({
  kind,
  siteID,
  value,
  onChange,
}: {
  kind: 'image' | 'file';
  siteID: string;
  value: string[];
  onChange: (value: unknown) => void;
}) {
  const [picking, setPicking] = useState(false);

  const move = (index: number, delta: number) => {
    const next = [...value];
    const [item] = next.splice(index, 1);

    if (item !== undefined) {
      next.splice(index + delta, 0, item);
      onChange(next);
    }
  };

  return (
    <div className={styles['media']}>
      {value.length > 0 && (
        <ul className={styles['media__list']}>
          {value.map((id, index) => (
            <li key={id} className={styles['media__tile']}>
              <MediaThumb id={id} />
              <div className={styles['media__tile-actions']}>
                <Button
                  iconOnly
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  aria-label="Раньше"
                  disabled={index === 0}
                  onClick={() => {
                    move(index, -1);
                  }}
                >
                  <ChevronLeft size={14} />
                </Button>
                <Button
                  iconOnly
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  aria-label="Позже"
                  disabled={index === value.length - 1}
                  onClick={() => {
                    move(index, 1);
                  }}
                >
                  <ChevronRight size={14} />
                </Button>
                <Button
                  iconOnly
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  aria-label="Убрать"
                  onClick={() => {
                    onChange(value.filter((item) => item !== id));
                  }}
                >
                  <X size={14} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button
        variant="outline"
        tone="neutral"
        iconLeft={<Plus size={15} />}
        onClick={() => {
          setPicking(true);
        }}
      >
        {kind === 'image' ? 'Добавить картинки' : 'Добавить файлы'}
      </Button>

      <MediaPicker
        open={picking}
        onOpenChange={setPicking}
        siteID={siteID}
        kind={kind}
        onChoose={(picked) => {
          onChange(value.includes(picked.id) ? value : [...value, picked.id]);
        }}
        onChooseMany={(picked) => {
          onChange([
            ...value,
            ...picked.map((item) => item.id).filter((id) => !value.includes(id)),
          ]);
        }}
      />
    </div>
  );
}

function MediaThumb({ id }: { id: string }) {
  const item = useQuery({
    queryKey: ['media-item', id],
    queryFn: async () => {
      const response = await mediaGet(id);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос файла');
      }

      return response.data;
    },
  }).data;

  if (item === undefined) {
    return <span className={styles['media__thumb']} />;
  }

  return item.thumb_url == null ? (
    <span className={styles['media__thumb']} title={item.name}>
      <Paperclip size={18} />
    </span>
  ) : (
    <AuthImage
      className={styles['media__thumb']}
      src={item.thumb_url}
      alt={item.alt === '' ? item.name : item.alt}
    />
  );
}

/** Значение поля нескольких файлов: список; один файл (поле переключили) — список из него. */
function asIDs(value: unknown): string[] {
  if (typeof value === 'string') {
    return value === '' ? [] : [value];
  }

  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

/** Значение поля одного файла; было несколько — первый. */
function asFirst(value: unknown): string {
  return asIDs(value)[0] ?? '';
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Число в текст для поля ввода.
 *
 * Ноль — осмысленное значение и обязан показываться; пусто — это отсутствие
 * значения, а не ноль, и подставлять его за человека нельзя.
 */
function asNumberText(value: unknown): string {
  return typeof value === 'number' ? String(value) : '';
}

/**
 * `datetime-local` не понимает ни часовой пояс, ни секунды — только
 * `2026-09-09T14:30`. Значение с сервера приходит полным, и без обрезки поле
 * молча остаётся пустым.
 */
function forDateTimeInput(value: string): string {
  return value.slice(0, 16);
}

function choices(field: FormField): string[] {
  const raw = field.options?.['choices'];

  return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === 'string') : [];
}

/**
 * Подсказка под полем вместе с пределами: «не длиннее 80 символов» человек
 * должен видеть до того, как упрётся в отказ, — с текущим счётом.
 */
function hintOf(field: FormField, value: unknown): string | undefined {
  const parts: string[] = [];

  if (field.hint !== '') {
    parts.push(field.hint);
  }

  const { max_length: maxLength, min_length: minLength } = field.rules;

  if ((maxLength !== undefined || minLength !== undefined) && field.type !== 'richtext') {
    const length = typeof value === 'string' ? [...value].length : 0;

    if (minLength === undefined && maxLength !== undefined) {
      parts.push(`${String(length)} из ${String(maxLength)} символов`);

      return parts.join(' · ');
    }

    const limits = [
      minLength === undefined ? '' : `от ${String(minLength)}`,
      maxLength === undefined ? '' : `до ${String(maxLength)}`,
    ]
      .filter((part) => part !== '')
      .join(' ');

    parts.push(`${String(length)} символов, нужно ${limits}`);
  }

  return parts.length === 0 ? undefined : parts.join(' · ');
}

/** Какие блоки разрешены полю «блоки»; пусто — любые. */
function allowedBlocks(field: FormField): string[] {
  const raw = field.options['allowed_blocks'];

  return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === 'string') : [];
}
